import { MIDASProject } from '../../types/project.types';
import { DependencyGraph, LineageNode, LineageEdge, LineageNodeType } from '../../types/lineage.types';
import { DataSet, DerivedDataSet, DataTransformOperationUnion } from '../../types/dataset.types';
import { TabState } from '../../types/pane.types';
import { Report, ReportElementUnion } from '../../types/report.types';
import { TrainedModel } from '../../types/model.types';
import logger from './logger';

// 型ガード関数
const isPrimaryDataSet = (dataset: DataSet): dataset is DataSet & { type: 'primary' } => {
  return dataset.type === 'primary';
};

const isDerivedDataSet = (dataset: DataSet): dataset is DerivedDataSet => {
  return dataset.type === 'derived';
};

/**
 * MIDASProjectから依存関係グラフを構築する
 */
export function buildDependencyGraph(project: MIDASProject, openTabs: TabState[]): DependencyGraph {
  const nodes: LineageNode[] = [];
  const edges: LineageEdge[] = [];
  
  
  // 1. DataSetノードを追加
  Object.values(project.datasets || {}).forEach((dataset: DataSet) => {
    // EphemeralDataSetは一時的なデータなのでlineageグラフに表示しない
    if (dataset.type === 'ephemeral') return;

    const nodeType: LineageNodeType = isPrimaryDataSet(dataset) ? 'primary-dataset' : 'derived-dataset';
    nodes.push({
      id: `dataset-${dataset.id}`,
      type: nodeType,
      label: dataset.name,
      data: dataset
    });
    
    // DerivedDataSetの場合、親への依存エッジを追加
    if (isDerivedDataSet(dataset)) {
      // parentDatasetIdsからのエッジ
      dataset.parentDatasetIds.forEach(parentId => {
        edges.push({
          id: `edge-${parentId}-to-${dataset.id}`,
          source: `dataset-${parentId}`,
          target: `dataset-${dataset.id}`,
          type: 'derives-from',
          label: getOperationLabel(dataset.operation)
        });
      });
    }
  });
  
  // 2. Reportノードを追加
  (project.reports || []).forEach((report: Report) => {
    nodes.push({
      id: `report-${report.id}`,
      type: 'report',
      label: report.name,
      data: report
    });
    
    // レポート要素から参照されるデータセットへのエッジ
    const referencedDatasetIds = new Set<string>();

    Object.values(report.elements).forEach((element: ReportElementUnion) => {
      if ('datasetId' in element && element.datasetId) {
        referencedDatasetIds.add(element.datasetId);
      }
      if (element.type === 'cross_dataset_comparison' && 'datasetIds' in element) {
        element.datasetIds.forEach((id: string) => referencedDatasetIds.add(id));
      }
    });
    
    referencedDatasetIds.forEach(datasetId => {
      edges.push({
        id: `edge-dataset-${datasetId}-to-report-${report.id}`,
        source: `dataset-${datasetId}`,
        target: `report-${report.id}`,
        type: 'references'
      });
    });
  });
  
  // 3. Modelノードを追加
  Object.values(project.models || {}).forEach((model: TrainedModel) => {
    nodes.push({
      id: `model-${model.id}`,
      type: 'model',
      label: model.name,
      data: model
    });
    
    // トレーニングデータセットへの参照
    if (model.metadata?.trainingDatasetId) {
      edges.push({
        id: `edge-dataset-${model.metadata.trainingDatasetId}-to-model-${model.id}`,
        source: `dataset-${model.metadata.trainingDatasetId}`,
        target: `model-${model.id}`,
        type: 'references',
        label: 'Trained on'
      });
    }
  });
  
  // 4. 開いているTabノードを追加
  openTabs.forEach(tab => {
    
    nodes.push({
      id: `tab-${tab.id}`,
      type: 'tab',
      label: tab.title,
      data: tab
    });
    
    // タブが参照するリソースへのエッジ
    if (tab.metadata) {
      if (tab.metadata.datasetId) {
        edges.push({
          id: `edge-dataset-${tab.metadata.datasetId}-to-tab-${tab.id}`,
          source: `dataset-${tab.metadata.datasetId}`,
          target: `tab-${tab.id}`,
          type: 'displays'
        });
      }
      if (tab.metadata.reportId) {
        edges.push({
          id: `edge-report-${tab.metadata.reportId}-to-tab-${tab.id}`,
          source: `report-${tab.metadata.reportId}`,
          target: `tab-${tab.id}`,
          type: 'displays'
        });
      }
      if (tab.metadata.modelId) {
        edges.push({
          id: `edge-model-${tab.metadata.modelId}-to-tab-${tab.id}`,
          source: `model-${tab.metadata.modelId}`,
          target: `tab-${tab.id}`,
          type: 'displays'
        });
      }
      if (tab.metadata.targetDatasetId) {
        edges.push({
          id: `edge-dataset-${tab.metadata.targetDatasetId}-to-tab-${tab.id}`,
          source: `dataset-${tab.metadata.targetDatasetId}`,
          target: `tab-${tab.id}`,
          type: 'references'
        });
      }
    }
  });
  
  logger.info('LineageGraphBuilder: Build complete', {
    totalNodes: nodes.length,
    nodeBreakdown: {
      primaryDatasets: nodes.filter(n => n.type === 'primary-dataset').length,
      derivedDatasets: nodes.filter(n => n.type === 'derived-dataset').length,
      reports: nodes.filter(n => n.type === 'report').length,
      tabs: nodes.filter(n => n.type === 'tab').length,
      models: nodes.filter(n => n.type === 'model').length
    },
    totalEdges: edges.length
  });
  
  return { nodes, edges };
}

/**
 * 操作タイプからラベルを取得
 */
function getOperationLabel(operation: DataTransformOperationUnion): string {
  if (!operation) return '';

  switch (operation.type) {
    case 'sql_query':
      return 'SQL Query';
    case 'crosstab':
      return 'Crosstab';
    case 'filter':
      return 'Filter';
    case 'glm_diagnostics':
      return 'GLM Diagnostics';
    case 'glm_prediction':
      return 'GLM Prediction';
    case 'dummy_coding':
      return 'Dummy Coding';
    default: {
      // Exhaustiveness check
      const _exhaustiveCheck: never = operation;
      void _exhaustiveCheck;
      return '';
    }
  }
}

/**
 * 指定ノードの上流依存関係を取得（データフローの上流）
 */
export function findUpstreamDependencies(
  graph: DependencyGraph,
  nodeId: string,
  visited: Set<string> = new Set()
): Set<string> {
  if (visited.has(nodeId)) return visited;
  visited.add(nodeId);
  
  // このノードをtargetとするエッジを探す（データフローの上流）
  const incomingEdges = graph.edges.filter(edge => edge.target === nodeId);
  
  incomingEdges.forEach(edge => {
    findUpstreamDependencies(graph, edge.source, visited);
  });
  
  return visited;
}

/**
 * 指定ノードの下流依存関係を取得（データフローの下流）
 */
export function findDownstreamDependents(
  graph: DependencyGraph,
  nodeId: string,
  visited: Set<string> = new Set()
): Set<string> {
  if (visited.has(nodeId)) return visited;
  visited.add(nodeId);
  
  // このノードをsourceとするエッジを探す（データフローの下流）
  const outgoingEdges = graph.edges.filter(edge => edge.source === nodeId);
  
  outgoingEdges.forEach(edge => {
    findDownstreamDependents(graph, edge.target, visited);
  });
  
  return visited;
}